import { api } from "@/api/client";

/**
 * Табель смен.
 *
 * Сотрудник видит смены своего подразделения (read-only), управляющий и
 * разработчик — все отделы и могут править. Права проверяет сервер,
 * `can_edit` здесь — только чтобы не показывать лишние кнопки.
 */

export type ShiftStatus =
  | "planned"
  | "worked"
  | "sick"
  | "absent"
  | "dayoff"
  | "vacation";

export interface ShiftItem {
  id: number;
  employee_id: number;
  day: string;
  status: ShiftStatus;
  status_label: string;
  short: string;
  starts_at: string | null;
  ends_at: string | null;
  note: string | null;
  moved_from: string | null;
  /** Учебная смена по плану обучения (учитывает табель). */
  is_training: boolean;
}

export interface ShiftPerson {
  employee_id: number;
  display_name: string;
  position: string | null;
  department_id: number | null;
  department_name: string | null;
}

export interface ShiftStatusMeta {
  value: ShiftStatus;
  label: string;
  short: string;
}

export interface ShiftWeek {
  start: string;
  end: string;
  today: string;
  can_edit: boolean;
  statuses: ShiftStatusMeta[];
  departments: { id: number; name: string }[];
  people: ShiftPerson[];
  shifts: ShiftItem[];
}

export const fetchShiftWeek = (
  start: string,
  options: { days?: number; departmentId?: number | null } = {},
) =>
  api<ShiftWeek>(
    `/api/shifts?start=${start}&days=${options.days ?? 7}` +
      (options.departmentId ? `&department_id=${options.departmentId}` : ""),
  );

export interface ShiftInput {
  status: ShiftStatus;
  starts_at?: string | null;
  ends_at?: string | null;
  note?: string | null;
}

export const saveShift = (
  employeeId: number,
  day: string,
  body: ShiftInput,
) =>
  api<ShiftItem>(`/api/shifts/${employeeId}/${day}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });

export const moveShift = (shiftId: number, day: string) =>
  api<ShiftItem>(`/api/shifts/${shiftId}/move`, {
    method: "POST",
    body: JSON.stringify({ day }),
  });

export const deleteShift = (shiftId: number) =>
  api<{ removed: boolean }>(`/api/shifts/${shiftId}`, { method: "DELETE" });

export interface BulkShiftInput {
  employee_ids: number[];
  /** Конкретные даты: «пн-ср» в даты переводит фронтенд. */
  days: string[];
  status: ShiftStatus;
  starts_at?: string | null;
  ends_at?: string | null;
  note?: string | null;
  /** false — заполнять только пустые клетки. */
  overwrite?: boolean;
}

export interface BulkResult {
  saved: number;
  skipped: number;
}

/** Шаблон недели и массовая правка: одна смена на много клеток сразу. */
export const bulkSaveShifts = (body: BulkShiftInput) =>
  api<BulkResult>("/api/shifts/bulk", {
    method: "POST",
    body: JSON.stringify(body),
  });

/** Копия недели: день недели сохраняется, занятые дни по умолчанию не трогаются. */
export const copyShiftWeek = (body: {
  from_start: string;
  to_start: string;
  employee_ids?: number[];
  department_id?: number | null;
  overwrite?: boolean;
}) =>
  api<BulkResult>("/api/shifts/copy-week", {
    method: "POST",
    body: JSON.stringify(body),
  });

export interface ShiftTemplateDay {
  /** 0 = понедельник … 6 = воскресенье. */
  weekday: number;
  status: ShiftStatus;
  starts_at: string | null;
  ends_at: string | null;
}

export interface ShiftTemplate {
  id: number;
  name: string;
  department_id: number | null;
  department_name: string | null;
  days: ShiftTemplateDay[];
}

/** Сохранённые шаблоны недели («официант 5/2»). */
export const fetchShiftTemplates = () =>
  api<ShiftTemplate[]>("/api/shifts/templates");

export const saveShiftTemplate = (body: {
  name: string;
  department_id?: number | null;
  days: {
    weekday: number;
    status: ShiftStatus;
    starts_at?: string | null;
    ends_at?: string | null;
  }[];
}) =>
  api<ShiftTemplate>("/api/shifts/templates", {
    method: "POST",
    body: JSON.stringify(body),
  });

/** Разложить шаблон на неделю: его дни → даты этой недели. */
export const applyShiftTemplate = (
  templateId: number,
  body: { employee_ids: number[]; week_start: string; overwrite?: boolean },
) =>
  api<BulkResult>(`/api/shifts/templates/${templateId}/apply`, {
    method: "POST",
    body: JSON.stringify(body),
  });

export const deleteShiftTemplate = (templateId: number) =>
  api<{ removed: boolean }>(`/api/shifts/templates/${templateId}`, {
    method: "DELETE",
  });

/** Массово снять смены с выбранных клеток. */
export const clearShiftCells = (body: {
  employee_ids: number[];
  days: string[];
}) =>
  api<{ removed: number }>("/api/shifts/clear", {
    method: "POST",
    body: JSON.stringify(body),
  });
